import { Flex, HStack, VStack, Box } from "@chakra-ui/react";
import { Button} from "@sgi/gravity/Button";
import { Input } from "@sgi/gravity/Input";
import { Typography } from "@sgi/gravity/Typography";
import { ErrorAlert } from "@sgi/gravity/Alert";
import Title from "../Common/Title";
import { 
  ChangeEvent, 
  KeyboardEvent, 
  useState, 
  useEffect,
  useCallback, 
  useRef 
} from "react";
import VerifiedIcon from "../Common/VerifiedIcon";
import { useWrapper } from "../Context/WrapperContext";
import { useResetMfaEmailProxy } from "../Custom Hooks/useResetMfaEmailProxy";
import  useFocusControl  from "../Custom Hooks/useFocusControl";
import { Link } from "@sgi/gravity";

export default function ResetMfaEmailVerify() {
  const { increment } = useWrapper();
  const [email, setEmail] = useState('');
  const [title, setTitle] = useState("");
  const [subText, setSubText] = useState("");
  const [isVerified, setIsVerified] = useState(false);
  const [buttonText, setButtonText] = useState("");
  const [helperText, setHelperText] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [hideCode, setHideCode] = useState(true);
  const [handleClick, setHandleClick] = useState(() => () => {});
  const [verificationCode, setVerificationCode] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const [emailErrorMessage, setEmailErrorMessage] = useState("");
  const [codeErrorMessage, setCodeErrorMessage] = useState("");

  const verifiedStyles = isVerified && {
    borderColor: "seagreen",
    borderWidth: "2px",
    boxShadow: "0 0 0 3px darkseagreen",
  };

  const {
    sendCodeAction,
    verifyCodeAction,
    sendNewCodeAction,
    changeEmailAction,
    pageLevelError,
    verificationError,
    emailError,
    cancelAction,
  } = useResetMfaEmailProxy({ email, verificationCode });

  const onEmailKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") sendCodeAction.act();
  };
  
  useEffect(() => setEmailErrorMessage(emailError), [emailError]);
  useEffect(() => setErrorMessage(pageLevelError), [pageLevelError]);
  useEffect(() => setCodeErrorMessage(verificationError), [verificationError]);
  
  const ref = useFocusControl(index => index === 0);

  const checkEmail = (e: ChangeEvent<HTMLInputElement>) => {
    const isValid = e.target.checkValidity();
    if (isValid) {
      setEmailErrorMessage("");
      setIsButtonDisabled(false);
    } else {
      setEmailErrorMessage(e.target.validationMessage);
      setIsButtonDisabled(true);
    }
  };

  const onEmailChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setEmail(value);
    setIsButtonDisabled(value === "");
    if (emailErrorMessage !== "") checkEmail(e);
  };

  const onVerifyChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setVerificationCode(value);
    setIsButtonDisabled(value === "");
    const isValid = /^\d{6}$/.test(value);
    setIsButtonDisabled(!isValid);    
  };
  const onVerificationCodeKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") verifyCodeAction.act();
  };

  const handleResendCodeClick = useCallback(() => {
    sendNewCodeAction.act();
  }, [sendNewCodeAction]);

  useEffect(() => {
      setTitle("Reset MFA");
      setSubText("To reset your Multi-Factor Authentication method, we will need to verify your email first. Please enter your registered email.")
  }, []);

  useEffect(() => {
    if (sendCodeAction?.visible) {
      setButtonText("Send verification code");
      setHelperText("We will send you a verification code");
      setIsButtonDisabled(true);
      setHandleClick(() => sendCodeAction.act);
      setHideCode(true);
      setIsVerified(false);
      setIsLoading(false);
      setEmail("");
      emailInputRef.current?.focus();
    } else if (verifyCodeAction?.visible && sendNewCodeAction?.visible) {
      setButtonText("Verify code");
      setHelperText("Verification code has been sent to your inbox");
      setIsButtonDisabled(true);
      setHandleClick(() => verifyCodeAction.act);
      setHideCode(false);
      setIsVerified(false);
      setIsLoading(false);
      setVerificationCode("");
      codeInputRef.current?.focus();
    } else if (changeEmailAction?.visible) {
      setButtonText("Change email address");
      setHelperText("");
      setIsButtonDisabled(false);
      setHandleClick(() => changeEmailAction.act);
      setHideCode(true);
      setIsVerified(true);
      setIsLoading(false);
      increment();
    } else {
      setIsLoading(true);
      setErrorMessage("");
    }
  }, [
    changeEmailAction,
    increment,
    sendCodeAction,
    sendNewCodeAction,
    verifyCodeAction,
  ]);

  const emailInputRef = useRef<HTMLInputElement>(null);
  const codeInputRef = useRef<HTMLInputElement>(null);

  return (
    <Flex ref={ref} 
          direction="column"
          maxW={544}
          padding={38}
          mx="auto"
          justifyContent="center"
          data-testid="enter-reset-mfa-email-test">
      <Title text={title} />
      <Typography variant="body" mb={8}>
        {subText}
      </Typography>
      <Box data-testid="error-containers">
        <Box data-testid="error-container">
          {errorMessage && (
            <ErrorAlert onClose={() => setErrorMessage("")}>
              {errorMessage}
            </ErrorAlert>
          )}
        </Box>
      </Box>
      <Input
        autoFocus
        ref={emailInputRef}
        my={1}
        label="Your Email"
        name="Email"
        id="email-field"
        type="email"
        style={{ backgroundColor: "white", ...verifiedStyles }}
        onChange={onEmailChange}
        onBlur={checkEmail}
        onKeyDown={onEmailKeyDown}
        placeholder="email@example.com"
        pattern="^[a-zA-Z0-9!#$%&'+^_`\{\}~\-]+(?:\.[a-zA-Z0-9!#$%&'+^_`\{\}~\-]+)*@(?:[a-zA-Z0-9](?:[a-zA-Z0-9\-]*[a-zA-Z0-9])?\.)+[a-zA-Z0-9](?:[a-zA-Z0-9\-]*[a-zA-Z0-9])?$"
        maxLength={73}
        error={emailErrorMessage}
        data-testid="reset-mfa-email-input"
        isReadOnly={isVerified}
        inputRightElement={isVerified && <VerifiedIcon />}
        spellCheck={false}
        isRequired={true}
        value={email}
        autoComplete="username"
        formHelperText={helperText}
      />
       <Input
        ref={codeInputRef}
        controlClassName="my-3"
        controlStyleProps={hideCode ? { display: "none" } : undefined}
        label="Verification code"
        name="Verification"
        id="reset-mfa-email-verify-code-field"
        type="text"
        style={{ backgroundColor: "white" }}
        onChange={onVerifyChange}
        onKeyDown={onVerificationCodeKeyDown}
        placeholder="Enter Verification code from email"
        error={codeErrorMessage}
        data-testid="code-input"
        isRequired={true}
        value={verificationCode}
        maxLength={6}
        pattern="\\d{6}"
        autoFocus
      />
      <VStack mt={6}>
        <Button
          isFullWidth
          onClick={handleClick}
          isLoading={isLoading}
          data-testid="action-button"
          isDisabled={isButtonDisabled}
        >
          {buttonText}
        </Button>
        <Button
                isFullWidth
                data-testid="cancel-button-test"
                onClick={cancelAction.act}
                my={2}
                variant="ghost"
                mt={3}
              >
                Cancel
              </Button>
        <HStack hidden={hideCode} textStyle="body">
          <span>Didn't receive the email?</span>
          <Link
            size="sm"
            href="#"
            onClick={handleResendCodeClick}
            data-testid="email-resend-code-button"
          >
            Resend code
          </Link>
        </HStack>
      </VStack>
    </Flex>
  );
}